import { useRef } from 'react';
import type { TrainingAnswer } from '@hcc/types';

const NUDGE = 0.02;

/**
 * Click-on-image answer: tapping the picture places a single marker; the
 * value is the normalised point. The wrapper is focusable and arrow keys
 * nudge the marker, so the question stays answerable without a pointer.
 * The correct regions never reach this component — the server strips them.
 */
export function HotspotAnswer({
  imageKey,
  value,
  onChange,
  disabled,
  ariaLabelledBy,
}: {
  imageKey: string;
  value: { x: number; y: number } | undefined;
  onChange: (next: TrainingAnswer) => void;
  disabled?: boolean;
  ariaLabelledBy?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);

  function place(e: React.PointerEvent) {
    if (disabled || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const y = Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height));
    onChange({ x: Math.round(x * 1000) / 1000, y: Math.round(y * 1000) / 1000 });
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (disabled) return;
    const deltas: Record<string, [number, number]> = {
      ArrowLeft: [-NUDGE, 0],
      ArrowRight: [NUDGE, 0],
      ArrowUp: [0, -NUDGE],
      ArrowDown: [0, NUDGE],
    };
    const d = deltas[e.key];
    if (!d) return;
    e.preventDefault();
    const cur = value ?? { x: 0.5, y: 0.5 };
    onChange({
      x: Math.min(1, Math.max(0, Math.round((cur.x + d[0]) * 1000) / 1000)),
      y: Math.min(1, Math.max(0, Math.round((cur.y + d[1]) * 1000) / 1000)),
    });
  }

  return (
    <div>
      <div
        ref={ref}
        role="application"
        tabIndex={disabled ? -1 : 0}
        aria-labelledby={ariaLabelledBy}
        aria-describedby="hotspot-help"
        onPointerUp={place}
        onKeyDown={onKeyDown}
        className="relative inline-block max-w-full cursor-crosshair touch-manipulation rounded-token-sm border border-line/10 focus:outline-none focus:ring-2 focus:ring-accent"
      >
        <img
          src={imageKey}
          alt="Tap the correct area of this picture"
          className="block max-h-96 max-w-full rounded-token-sm"
          draggable={false}
        />
        {value ? (
          <span
            aria-hidden
            style={{ left: `${value.x * 100}%`, top: `${value.y * 100}%` }}
            className="absolute h-6 w-6 -translate-x-1/2 -translate-y-1/2 rounded-full border-4 border-accent bg-accent/30 shadow-token-sm"
          />
        ) : null}
      </div>
      <p id="hotspot-help" className="mt-1.5 text-xs text-fg-muted" aria-live="polite">
        {value
          ? `Marker placed at ${Math.round(value.x * 100)}%, ${Math.round(value.y * 100)}% — tap again or use the arrow keys to adjust.`
          : 'Tap the picture to place your answer, or focus it and use the arrow keys.'}
      </p>
    </div>
  );
}
